/**
 * Edit Billing Identity Modal
 *
 * Modal for editing an existing billing identity's details.
 */

import { useEffect, useState } from 'react';
import { useBillingEntityOperations, useBillingEntityOperationsAdmin } from '@features/billing-entities';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { BillingEntityResponse } from '@store/generated/billing-entities';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../utils/errorUtils';

/**
 * Core behavioral health clinician taxonomies
 */
const TAXONOMY_CODES = [
  { code: '2084P0800X', name: 'Psychiatrist' },
  { code: '2084P0804X', name: 'Child & Adolescent Psychiatrist' },
  { code: '2084A0401X', name: 'Addiction Psychiatrist' },
  { code: '103T00000X', name: 'Psychologist' },
  { code: '103TC0700X', name: 'Clinical Psychologist' },
  { code: '1041C0700X', name: 'Licensed Clinical Social Worker (LCSW)' },
  { code: '101YP2500X', name: 'Licensed Professional Counselor (LPC / LMHC)' },
  { code: '106H00000X', name: 'Marriage & Family Therapist (LMFT)' },
  { code: '103G00000X', name: 'Clinical Neuropsychologist' },
  { code: '363LP0808X', name: 'Psychiatric Nurse Practitioner (PMHNP)' },
];

interface EditBillingEntityModalProps {
  open: boolean;
  onClose: () => void;
  entity: BillingEntityResponse;
  onUpdated: () => void;
  /** When provided, uses admin API routes (for admin panel context) */
  organizationId?: string;
}

interface FormData {
  billing_npi: string;
  tax_id: string;
  tax_id_type: string;
  taxonomy_code: string;
  legal_name: string;
  doing_business_as: string;
  billing_address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

export function EditBillingEntityModal({
  open,
  onClose,
  entity,
  onUpdated,
  organizationId,
}: EditBillingEntityModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const providerOps = useBillingEntityOperations();
  const adminOps = useBillingEntityOperationsAdmin();
  const { updateLoading } = organizationId ? adminOps : providerOps;
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initialize form with entity data
  const [formData, setFormData] = useState<FormData>({
    billing_npi: entity.billing_npi || '',
    tax_id: '', // Tax ID is masked, user needs to re-enter if changing
    tax_id_type: entity.tax_id_type || 'EIN',
    taxonomy_code: entity.taxonomy_code || '',
    legal_name: entity.legal_name || '',
    doing_business_as: entity.doing_business_as || '',
    billing_address: {
      line1: entity.billing_address?.line1 || '',
      line2: entity.billing_address?.line2 || '',
      city: entity.billing_address?.city || '',
      state: entity.billing_address?.state || '',
      zip: entity.billing_address?.zip || '',
    },
    contact_name: entity.contact_name || '',
    contact_email: entity.contact_email || '',
    contact_phone: entity.contact_phone || '',
  });

  // Reset form when entity changes
  useEffect(() => {
    setFormData({
      billing_npi: entity.billing_npi || '',
      tax_id: '',
      tax_id_type: entity.tax_id_type || 'EIN',
      taxonomy_code: entity.taxonomy_code || '',
      legal_name: entity.legal_name || '',
      doing_business_as: entity.doing_business_as || '',
      billing_address: {
        line1: entity.billing_address?.line1 || '',
        line2: entity.billing_address?.line2 || '',
        city: entity.billing_address?.city || '',
        state: entity.billing_address?.state || '',
        zip: entity.billing_address?.zip || '',
      },
      contact_name: entity.contact_name || '',
      contact_email: entity.contact_email || '',
      contact_phone: entity.contact_phone || '',
    });
    setSubmitError(null);
  }, [entity]);

  const handleChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddressChange =
    (field: keyof FormData['billing_address']) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        billing_address: {
          ...prev.billing_address,
          [field]: event.target.value,
        },
      }));
    };

  const handleSubmit = async () => {
    setSubmitError(null);

    // Build update request
    const updateRequest: Record<string, unknown> = {
      taxonomy_code: formData.taxonomy_code.trim() || null,
      legal_name: formData.legal_name.trim(),
      doing_business_as: formData.doing_business_as.trim() || null,
      billing_address: {
        line1: formData.billing_address.line1.trim(),
        line2: formData.billing_address.line2.trim() || null,
        city: formData.billing_address.city.trim(),
        state: formData.billing_address.state.trim().toUpperCase(),
        zip: formData.billing_address.zip.trim(),
      },
      contact_name: formData.contact_name.trim() || null,
      contact_email: formData.contact_email.trim(),
      contact_phone: formData.contact_phone.trim(),
    };

    // Only include NPI and tax fields if entity is NOT synced
    if (!entity.stedi_synced) {
      updateRequest.billing_npi = formData.billing_npi.replace(/\D/g, '');
      
      // Only include tax_id if user entered a new one
      if (formData.tax_id.trim()) {
        updateRequest.tax_id = formData.tax_id.replace(/\D/g, '');
        updateRequest.tax_id_type = formData.tax_id_type;
      }
    }

    try {
      if (organizationId) {
        await adminOps.update({
          organizationId,
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      } else {
        await providerOps.update({
          billingEntityId: entity.billing_entity_id,
          updateBillingEntityRequest: updateRequest,
        }).unwrap();
      }

      enqueueSnackbar('Changes saved. You may need to verify your billing identity again.', {
        variant: 'success',
      });
      onUpdated();
      onClose();
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to update billing identity. Please check your information and try again.',
      );
      setSubmitError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Edit Billing Identity</span>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {entity.stedi_synced && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This identity has been verified. NPI and Tax ID cannot be changed after verification. To
            use a different NPI or Tax ID, create a new billing identity.
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* NPI */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Billing NPI"
              value={formData.billing_npi}
              onChange={handleChange('billing_npi')}
              fullWidth
              required
              disabled={entity.stedi_synced}
              helperText={
                entity.stedi_synced
                  ? 'Cannot be changed after verification'
                  : '10-digit National Provider Identifier'
              }
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* Tax ID Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={entity.stedi_synced}>
              <InputLabel>Tax ID Type</InputLabel>
              <Select
                value={formData.tax_id_type}
                label="Tax ID Type"
                onChange={e => setFormData(prev => ({ ...prev, tax_id_type: e.target.value }))}
              >
                <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
                <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Tax ID */}
          <Grid item xs={12}>
            <TextField
              label={`Tax ID (${formData.tax_id_type})`}
              value={entity.stedi_synced ? entity.tax_id_masked : formData.tax_id}
              onChange={handleChange('tax_id')}
              fullWidth
              disabled={entity.stedi_synced}
              placeholder={entity.stedi_synced ? '' : `Current: ${entity.tax_id_masked} (leave blank to keep)`}
              helperText={
                entity.stedi_synced
                  ? 'Cannot be changed after verification'
                  : 'Only enter if you need to change the Tax ID'
              }
            />
          </Grid>

          {/* Taxonomy Code */}
          <Grid item xs={12}>
            <Autocomplete
              value={TAXONOMY_CODES.find(t => t.code === formData.taxonomy_code) || null}
              onChange={(_event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  taxonomy_code: newValue?.code || '',
                }));
              }}
              options={TAXONOMY_CODES}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {option.code}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Taxonomy Code"
                  required
                  helperText="Provider specialty/credential type"
                />
              )}
            />
          </Grid>

          {/* Legal Name */}
          <Grid item xs={12}>
            <TextField
              label="Legal Name"
              value={formData.legal_name}
              onChange={handleChange('legal_name')}
              fullWidth
              required
              helperText="Full legal name of the entity"
            />
          </Grid>

          {/* Doing Business As (optional) */}
          <Grid item xs={12}>
            <TextField
              label="Doing Business As (DBA)"
              value={formData.doing_business_as}
              onChange={handleChange('doing_business_as')}
              fullWidth
              helperText="Optional - if different from legal name"
            />
          </Grid>

          {/* Address Line 1 */}
          <Grid item xs={12}>
            <TextField
              label="Address Line 1"
              value={formData.billing_address.line1}
              onChange={handleAddressChange('line1')}
              fullWidth
              required
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid item xs={12}>
            <TextField
              label="Address Line 2"
              value={formData.billing_address.line2}
              onChange={handleAddressChange('line2')}
              fullWidth
              helperText="Suite, unit, etc. (optional)"
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              value={formData.billing_address.city}
              onChange={handleAddressChange('city')}
              fullWidth
              required
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              value={formData.billing_address.state}
              onChange={handleAddressChange('state')}
              fullWidth
              required
              helperText="2-letter code (e.g., CA)"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* ZIP */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="ZIP Code"
              value={formData.billing_address.zip}
              onChange={handleAddressChange('zip')}
              fullWidth
              required
              helperText="XXXXX or XXXXX-XXXX"
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Contact Information
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Required for clearinghouse communication about claims
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Name"
              value={formData.contact_name}
              onChange={handleChange('contact_name')}
              fullWidth
              helperText="Optional"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange('contact_email')}
              fullWidth
              required
              helperText="For claim status notifications"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Phone"
              value={formData.contact_phone}
              onChange={handleChange('contact_phone')}
              fullWidth
              required
              helperText="For urgent claim issues"
            />
          </Grid>
        </Grid>

        {submitError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updateLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateLoading}
          startIcon={updateLoading ? <CircularProgress size={20} /> : undefined}
        >
          {updateLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
